/**
 * ストリーミング経路の録音後 / SOAP 生成中 / SOAP 表示統合画面.
 *
 * 本体レンダリングは `@/apps/legacy/features/yakureki/screens/PostRecordingScreenContent`
 * を再利用し (legacy にも依存しない)、streaming 固有の関心事 (overlay 判定 +
 * mount 時 refetch) のみここで上書きする。
 *
 * - overlay 判定は `patientDetail.status === 'processing' || soaps == null` の
 *   data-driven 条件とする。legacy の `patientList.status === 'processing'` 経路は
 *   2s polling が PROCESSING を跨がない streaming 高速経路で描画漏れする場合が
 *   あるため、patientDetail 直参照 + SOAP 有無で冗長に判定する。
 *   ただし `failed` / `failed_short` の terminal 失敗状態では SOAP は永久に
 *   到着しないため第二項 (soaps==null) を抑制し、SoapContentArea の失敗 UI を
 *   見えるようにする。
 * - mount 時に `reloadPatientDetail()` を 1 回だけ走らせ、screen 遷移直後の
 *   stale patientDetail を矯正する.
 *
 * 今後 streaming 固有の UI 拡張 (transcript 逐次プレビュー、話者分離トグル、
 * SOAP との対比表示等) を追加する場合は、本ファイル内で overlay と本体の
 * 合成を進めるか、必要に応じて content を使わずに streaming 専用本体を
 * 切り出す (その際は C' 化 = hook 物理分離も要検討).
 */
import { FC, useEffect, useRef } from 'react'
import { PostRecordingScreenContent } from '@/apps/legacy/features/yakureki/screens/PostRecordingScreenContent'
import type { PostRecordingScreenProps } from '@/apps/legacy/features/yakureki/PostRecordingScreen'
import {
  isFailureStatus,
  isInProgressStatus,
} from '@/shared/types/yakurekiCore'

export const StreamingPostRecordingScreen: FC<PostRecordingScreenProps> = (
  props
) => {
  const { patientDetail, reloadPatientDetail } = props

  // mount 時に patientDetail を一度 refetch.
  // usePatientDetailQuery は status='processing' を自身の data で観測しないと
  // polling を開始しないため、stale な data で screen に入ると更新が止まる
  // ケースがある。ここで 1 回強制 refetch することで最新 status/soaps を得る.
  // reloadPatientDetail は毎 render で再生成されるため ref で最新を指した上で
  // 初回 mount の effect から読む (deps 空で 1 回だけ実行、ref は effect 内で更新).
  const reloadRef = useRef(reloadPatientDetail)
  useEffect(() => {
    reloadRef.current = reloadPatientDetail
  })
  useEffect(() => {
    reloadRef.current()
  }, [])

  // 失敗 status では SOAP は生成されないので soaps==null 経路の overlay を出さない.
  // 出してしまうと SoapContentArea の文字起こしエラー UI / 再生成ボタンが永久に
  // 隠れ、特に failed_short ではヘッダ再生成も無いためユーザーが詰む.
  const overlayVisible =
    isInProgressStatus(patientDetail.status) ||
    (patientDetail.soaps == null && !isFailureStatus(patientDetail.status))

  return (
    <PostRecordingScreenContent {...props} overlayVisible={overlayVisible} />
  )
}
